import { memo, type RefObject, useEffect, useRef } from 'react';

export type WorkspaceAgentMentionListSentinelProps = {
  isFetching: boolean;
  listRef: RefObject<HTMLElement | null>;
  onFetch: () => void;
  rootMargin: string;
};

/** Prefetches the next page when the last loaded child of one parent nears the fold. */
export const WorkspaceAgentMentionListSentinel = memo(
  ({ isFetching, listRef, onFetch, rootMargin }: WorkspaceAgentMentionListSentinelProps) => {
    const sentinelRef = useRef<HTMLDivElement>(null);
    const onFetchRef = useRef(onFetch);
    onFetchRef.current = onFetch;

    useEffect(() => {
      const listElement = listRef.current;
      const sentinel = sentinelRef.current;

      if (isFetching || !listElement || !sentinel) {
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            onFetchRef.current();
          }
        },
        { root: listElement, rootMargin },
      );

      observer.observe(sentinel);

      return () => observer.disconnect();
    }, [isFetching, listRef, rootMargin]);

    return <div aria-hidden className="h-px w-full shrink-0" ref={sentinelRef} />;
  },
);

WorkspaceAgentMentionListSentinel.displayName = 'WorkspaceAgentMentionListSentinel';
